import CopyButton from "@/components/copy-button";
import { Button } from "@/components/ui/button";
import { usePublishReport, useUnPublishReport } from "@/hooks/use-postmaster";
import { SITE_BASE_URL } from "@/lib/constants";
import {
  EmailHealthHistoryResponse,
  EmailHealthResult,
} from "@/types/email-health";
import { DnsRecordsResponse } from "@/types/postman.types";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const PublishReportButton = ({
  companyId,
  domain,
  reportId,
  isPublic: isPublicProp,
  type,
}: {
  companyId: string;
  domain: string;
  reportId: string;
  isPublic: boolean;
  type: "history-table" | "details-page";
}) => {
  const { mutateAsync: publishReport, isPending: isPublishing } =
    usePublishReport();

  const { mutateAsync: unPublishReport, isPending: isUnPublishing } =
    useUnPublishReport();

  const queryClient = useQueryClient();

  const handlePublish = async () => {
    try {
      if (isPublicProp) {
        await unPublishReport({
          domain,
          reportIds: [reportId],
        });
        window.cioanalytics?.track("email_health_test_unpublished", {
          domain: domain,
        });
      } else {
        await publishReport({
          domain,
          reportIds: [reportId],
        });
        window.cioanalytics?.track("email_health_test_published", {
          domain: domain,
        });
      }

      try {
        //update full report data
        queryClient.setQueryData(
          ["stored-dns-records", domain],
          (oldData: DnsRecordsResponse) => {
            const update = isPublicProp
              ? {
                  isPublic: false,
                  publishedAt: null,
                }
              : {
                  isPublic: true,
                  publishedAt: new Date().toISOString(),
                };
            return Object.assign({}, oldData, update);
          }
        );

        // update the history table
        const activePageQueries = queryClient
          .getQueryCache()
          .findAll({ queryKey: ["emailHealthHistory"] }) // match all pagination queries
          .filter((query) => query.isActive());

        if (activePageQueries.length === 0) {
          throw new Error("No active page queries found");
        }

        const activePageQuery = activePageQueries[0];

        const oldData = activePageQuery.state
          .data as EmailHealthHistoryResponse;

        const records = oldData.data as EmailHealthResult[];

        const updatedRecords = records.map((record) => {
          if (record._id === reportId) {
            const update = isPublicProp
              ? {
                  isPublic: false,
                  publishedAt: null,
                }
              : {
                  isPublic: true,
                  publishedAt: new Date().toISOString(),
                };
            return Object.assign({}, record, update);
          }
          return record;
        });

        queryClient.setQueryData(activePageQuery.queryKey, {
          ...oldData,
          data: updatedRecords,
        });
      } catch (error) {
        console.error(error);

        if (type === "history-table") {
          queryClient.invalidateQueries({
            queryKey: ["emailHealthHistory"],
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: ["stored-dns-records", domain],
          });
        }
      }

      toast.success(
        `Report ${isPublicProp ? "unpublished" : "published"} successfully`
      );
    } catch (error) {
      toast.error(`Failed to ${isPublicProp ? "unpublish" : "publish"} report`);
    }
  };

  return (
    <div className="h-full flex items-center justify-center gap-2">
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={handlePublish}
        disabled={isPublishing || isUnPublishing}
        isLoading={isPublishing || isUnPublishing}
      >
        {isPublicProp ? "Unpublish" : "Publish"}
      </Button>

      {isPublicProp && (
        <CopyButton
          text={`${SITE_BASE_URL}/shared-report/${domain}/${companyId}/${reportId}`}
        />
      )}
    </div>
  );
};

export default PublishReportButton;
